import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import IntegrationCard from '../../components/settings/IntegrationCard';
import {
  useDisconnectIntegration,
  useIntegrations,
  useStartGoogleConnect,
} from '../../hooks/useIntegrations';

const ERROR_REASON_COPY: Record<string, string> = {
  invalid_state: 'Connection failed — please try again.',
  user_mismatch:
    'You appear to have switched accounts. Sign back in and try again.',
  token_exchange_failed: 'Google rejected the connection. Please try again.',
  missing_account_email: 'Could not read your Google account. Please try again.',
  access_denied: 'Connection was canceled.',
};

const IntegrationsSettings: React.FC = () => {
  const { data: connections = [], isLoading, isError, refetch } = useIntegrations();
  const startConnect = useStartGoogleConnect();
  const disconnect = useDisconnectIntegration();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle OAuth callback query params
  useEffect(() => {
    const status = searchParams.get('status');
    const provider = searchParams.get('provider');
    const accountEmail = searchParams.get('account_email');
    const reason = searchParams.get('reason');
    if (!status) return;

    if (status === 'success') {
      const providerName = provider === 'google' ? 'Google Calendar' : 'integration';
      toast.success(
        accountEmail
          ? `${providerName} connected — ${accountEmail}`
          : `${providerName} connected`
      );
      void refetch();
    } else if (status === 'error') {
      toast.error(
        ERROR_REASON_COPY[reason ?? ''] || 'Connection failed — please try again.'
      );
    }

    // Strip the OAuth params so a refresh doesn't re-fire
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('provider');
    next.delete('account_email');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refetch]);

  const googleConnection =
    connections.find((c) => c.provider === 'google') ?? null;

  return (
    <section className="space-y-6">
      <header>
        <h2 className="text-xl font-bold text-text-primary">Integrations</h2>
        <p className="text-sm text-text-secondary mt-1">
          Connect Tereiko with the tools your team already uses.
        </p>
      </header>

      {isLoading ? (
        <div className="card-elevated p-6">
          <div className="animate-pulse flex items-start gap-4">
            <div className="w-12 h-12 rounded-2 bg-surface-elevated" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 rounded bg-surface-elevated" />
              <div className="h-3 w-64 rounded bg-surface-elevated" />
            </div>
          </div>
        </div>
      ) : isError ? (
        <div className="card-elevated p-6 text-sm text-text-secondary">
          Couldn&apos;t load your integrations.{' '}
          <button
            type="button"
            onClick={() => refetch()}
            className="text-primary-500 hover:underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <IntegrationCard
            provider="google"
            connection={googleConnection}
            startConnect={startConnect}
            disconnect={disconnect}
          />
        </div>
      )}
    </section>
  );
};

export default IntegrationsSettings;
